/**
 * Reine Layout-Logik für ein-/ausblendbare, umsortierbare Seiten-Blöcke
 * (BL-219). Getrennt von `use-block-layout.ts` (React-Hook + localStorage),
 * damit die Kernlogik ohne React-Renderer in Vitest getestet werden kann —
 * gleiches Muster wie `permissions.ts`/`use-permissions.ts` (BL-155): ein
 * React-Hook-Import zieht transitiv React und kann in Vitest/Node einen
 * ESM/CJS-Modul-Zyklus auslösen, unabhängig vom eigentlichen Testinhalt.
 */

export interface StoredBlockLayout {
  /** Alle Block-IDs in konfigurierter Reihenfolge (inkl. ausgeblendeter). */
  order: string[];
  /** Ausgeblendete Block-IDs (Teilmenge von `order`). */
  hidden: string[];
}

/**
 * Gespeicherte Reihenfolge gegen die aktuell im Code bekannten Block-IDs
 * abgleichen: neue Blöcke (seit dem letzten Save hinzugekommen) werden ans
 * Ende angehängt, entfernte Blöcke aus einer alten gespeicherten Reihenfolge
 * rausgefiltert. Ohne das würde ein Code-Update einen neuen Block für
 * Bestands-User unsichtbar machen (fehlt einfach in `order`) statt ihn
 * sichtbar ans Ende zu setzen.
 */
export function reconcileBlockLayout(
  stored: StoredBlockLayout | null,
  defaultOrder: string[],
): StoredBlockLayout {
  if (!stored) return { order: defaultOrder, hidden: [] };
  const known = new Set(defaultOrder);
  const order = stored.order.filter((id) => known.has(id));
  for (const id of defaultOrder) {
    if (!order.includes(id)) order.push(id);
  }
  const hidden = stored.hidden.filter((id) => known.has(id));
  return { order, hidden };
}

export function toggleBlockVisible(layout: StoredBlockLayout, id: string): StoredBlockLayout {
  return {
    ...layout,
    hidden: layout.hidden.includes(id)
      ? layout.hidden.filter((h) => h !== id)
      : [...layout.hidden, id],
  };
}

/** Tauscht Block `id` mit seinem Nachbarn (-1 = nach oben, 1 = nach unten). No-op am Rand. */
export function moveBlock(
  layout: StoredBlockLayout,
  id: string,
  direction: -1 | 1,
): StoredBlockLayout {
  const idx = layout.order.indexOf(id);
  const swapWith = idx + direction;
  if (idx < 0 || swapWith < 0 || swapWith >= layout.order.length) return layout;
  const order = [...layout.order];
  [order[idx], order[swapWith]] = [order[swapWith]!, order[idx]!];
  return { ...layout, order };
}

export function visibleBlockIds(layout: StoredBlockLayout): string[] {
  return layout.order.filter((id) => !layout.hidden.includes(id));
}

/** Parst einen localStorage-Rohwert defensiv — `null` bei fehlendem/kaputtem Eintrag. */
export function parseStoredBlockLayout(raw: string | null): StoredBlockLayout | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredBlockLayout>;
    if (!Array.isArray(parsed.order) || !Array.isArray(parsed.hidden)) return null;
    return { order: parsed.order, hidden: parsed.hidden };
  } catch {
    return null;
  }
}
